// src/utils/spreadsheetFormulas.js
// A small formula engine for ExamSpreadsheetGrid, deliberately scoped to the
// functions ACCA's own CBE spreadsheet guidance lists as commonly used across
// its Strategic Professional papers (SBR/AFM/APM/ATX/AAA) and F5-F9: SUM,
// AVERAGE, ROUND, POWER (and the ^ operator), SQRT, COUNT, LOG, LN, EXP,
// NPV, IRR, MIRR and NORMSDIST, plus +-*/ arithmetic and A1-style cell
// references/ranges, with $ for absolute references ($A$1, A$1, $A1). Not
// paper-specific - this platform is used across multiple ACCA papers/tutors,
// and this is ACCA's own general function list, not something scoped to any
// one paper.
//
// Also owns cell number formats (formatWithNumFormat - thousands
// separators, decimals, currency, percentage, brackets for negatives), so
// the grid, the marker's cell viewer and the marking PDF all show the same
// formatted value from evaluateGrid's `display`.
//
// Not shared with functions/index.js's abandoned-session recovery renderer
// (buildRecoveredSubmission/gridJsonToTextRows) - that path is deliberately
// simpler and only ever shows raw cell text, same reasoning as its own
// comment already documents for the rest of that renderer.

class FormulaError extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

function isNumericLiteral(str) {
  // Accepts a leading-zero-less decimal too (".2", "-.2"), same as Excel
  // itself does - not just "0.2".
  return /^-?(\d+(\.\d+)?|\.\d+)$/.test(str.trim());
}

// The number a typed (non-formula) cell value stands for, or null if it's
// text. Wider than isNumericLiteral above (which stays strict for numbers
// INSIDE a formula, where a comma separates arguments): accepts the ways
// Excel itself accepts a typed number, which is how accountancy students
// naturally write them - thousands separators ("970,000"), accounting-style
// brackets for negatives ("(3,000)"), a currency sign ("£311,000"), a
// percentage ("40%" = 0.4), surrounding spaces (common when pasting from
// Excel), and the en dash/minus sign some keyboards autocorrect "-" into.
// Before this, any of those evaluated as text - so =C17-C18 gave #VALUE!
// and SUM silently skipped them, returning a wrong total.
function parseTypedNumber(raw) {
  let s = String(raw).replace(/ /g, ' ').trim();
  if (!s) return null;
  let negative = false;
  let percent = false;
  if (/^\(.*\)$/.test(s)) { negative = true; s = s.slice(1, -1).trim(); }
  if (s.endsWith('%')) { percent = true; s = s.slice(0, -1).trim(); }
  if (/^[-−–+]/.test(s)) { if (s[0] !== '+') negative = !negative; s = s.slice(1).trim(); }
  s = s.replace(/^[£$€]\s*/, '');
  if (/^[-−–]/.test(s)) { negative = !negative; s = s.slice(1).trim(); } // "£-3,000"
  if (!/^(\d{1,3}(,\d{3})+(\.\d+)?|\d+(\.\d+)?|\.\d+)$/.test(s)) return null;
  let n = parseFloat(s.replace(/,/g, ''));
  if (percent) n /= 100;
  return negative ? -n : n;
}

// A cell's optional number format: { kind: 'number' | 'currency' |
// 'percent', decimals, thousands, negBrackets, symbol }. Only changes how a
// numeric value is shown - the value itself (and every formula using it) is
// untouched, exactly like Excel.
export function formatWithNumFormat(n, fmt) {
  if (!isFinite(n)) return '#NUM!';
  const decimals = Math.min(10, Math.max(0, Math.round(Number(fmt.decimals) || 0)));
  const v = fmt.kind === 'percent' ? n * 100 : n;
  const abs = Math.abs(v);
  let body = fmt.thousands
    ? abs.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : abs.toFixed(decimals);
  if (fmt.kind === 'currency') body = (fmt.symbol || '$') + body;
  if (fmt.kind === 'percent') body += '%';
  const isNegative = v < 0 && Number(abs.toFixed(decimals)) !== 0;
  if (!isNegative) return body;
  return fmt.negBrackets ? `(${body})` : `-${body}`;
}

function isNumFormat(fmt) {
  return !!fmt && typeof fmt === 'object' && ['number', 'currency', 'percent'].includes(fmt.kind);
}

// How many decimal places a value is currently showing - what Increase/
// Decrease Decimal start from on a cell that has no number format yet.
export function visibleDecimals(display) {
  const m = /\.(\d+)/.exec(String(display || ''));
  return m ? m[1].length : 0;
}

// Moves every relative cell reference in a formula by (dr, dc), leaving $
// (absolute) parts alone - what Excel does when a formula is copied/pasted
// or filled into another cell. A reference pushed off the top/left edge
// becomes #REF!, same as Excel. Function names (SUM, LOG...) are never
// mistaken for references since a reference must have row digits and
// can't be followed by "(".
export function shiftFormula(formula, dr, dc) {
  if (typeof formula !== 'string' || !formula.trim().startsWith('=') || (!dr && !dc)) return formula;
  return formula.replace(/(\$?)([A-Za-z]{1,3})(\$?)(\d+)(?![\d(])/g, (match, colAbs, letters, rowAbs, digits, offset, whole) => {
    const prev = offset > 0 ? whole[offset - 1] : '';
    if (/[A-Za-z0-9_.]/.test(prev)) return match;
    let col = 0;
    const upper = letters.toUpperCase();
    for (let i = 0; i < upper.length; i++) col = col * 26 + (upper.charCodeAt(i) - 64);
    col -= 1;
    let row = parseInt(digits, 10) - 1;
    if (!colAbs) col += dc;
    if (!rowAbs) row += dr;
    if (col < 0 || row < 0) return '#REF!';
    let colLetters = '';
    let n = col + 1;
    while (n > 0) {
      const rem = (n - 1) % 26;
      colLetters = String.fromCharCode(65 + rem) + colLetters;
      n = Math.floor((n - 1) / 26);
    }
    return `${colAbs}${colLetters}${rowAbs}${row + 1}`;
  });
}

function formatNumber(n) {
  if (!isFinite(n)) return '#NUM!';
  // Kill float noise (0.1+0.2 etc.) without imposing a fixed decimal count.
  const rounded = Math.round(n * 1e9) / 1e9;
  return String(rounded);
}

function cellRefToRC(ref) {
  const m = /^([A-Z]+)([0-9]+)$/.exec(ref);
  if (!m) throw new FormulaError('#REF!');
  const [, letters, digits] = m;
  let col = 0;
  for (let i = 0; i < letters.length; i++) col = col * 26 + (letters.charCodeAt(i) - 64);
  col -= 1;
  const row = parseInt(digits, 10) - 1;
  return [row, col];
}

// ---- Tokenizer ----

function tokenize(src) {
  const tokens = [];
  const upper = src.toUpperCase(); // formulas are case-insensitive, like Excel
  let i = 0;
  const single = { '+': 'PLUS', '-': 'MINUS', '*': 'STAR', '/': 'SLASH', '^': 'CARET', '(': 'LPAREN', ')': 'RPAREN', ',': 'COMMA', ':': 'COLON' };
  while (i < upper.length) {
    const ch = upper[i];
    if (/\s/.test(ch)) { i++; continue; }
    if (/[0-9.]/.test(ch)) {
      let j = i;
      while (j < upper.length && /[0-9.]/.test(upper[j])) j++;
      const numStr = upper.slice(i, j);
      if (!isNumericLiteral(numStr)) throw new FormulaError('#ERROR!');
      tokens.push({ type: 'NUMBER', value: parseFloat(numStr) });
      i = j;
      continue;
    }
    if (ch === '$' || /[A-Z]/.test(ch)) {
      // A cell reference, optionally with $ (absolute) markers - $A$1, A$1,
      // $A1. The $ only matters when a formula is copied/filled elsewhere
      // (see shiftFormula); for evaluation it's the same cell.
      // Plain references (A1) are still handled by the original code below.
      const ref = /^\$?([A-Z]+)\$?([0-9]+)/.exec(upper.slice(i));
      if (ref && ref[0].includes('$')) {
        tokens.push({ type: 'CELL', value: ref[1] + ref[2] });
        i += ref[0].length;
        continue;
      }
      if (ch === '$') throw new FormulaError('#ERROR!');
      let j = i;
      while (j < upper.length && /[A-Z]/.test(upper[j])) j++;
      const letters = upper.slice(i, j);
      let k = j;
      while (k < upper.length && /[0-9]/.test(upper[k])) k++;
      if (k > j) {
        tokens.push({ type: 'CELL', value: letters + upper.slice(j, k) });
        i = k;
      } else {
        tokens.push({ type: 'IDENT', value: letters });
        i = j;
      }
      continue;
    }
    if (single[ch]) { tokens.push({ type: single[ch] }); i++; continue; }
    throw new FormulaError('#ERROR!');
  }
  tokens.push({ type: 'EOF' });
  return tokens;
}

// ---- Recursive-descent parser ----
// expr := term (('+'|'-') term)*
// term := power (('*'|'/') power)*
// power := unary ('^' power)?          (right-associative)
// unary := '-' unary | '+' unary | primary
// primary := NUMBER | CELL (':' CELL)? | IDENT '(' args ')' | '(' expr ')'
// An argument to a function call may additionally be a bare range (A1:B5).

function parseFormula(src) {
  const tokens = tokenize(src);
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const expect = (type) => {
    if (peek().type !== type) throw new FormulaError('#ERROR!');
    return next();
  };

  function parseExpr() {
    let node = parseTerm();
    while (peek().type === 'PLUS' || peek().type === 'MINUS') {
      const op = next().type;
      node = { type: 'binop', op, left: node, right: parseTerm() };
    }
    return node;
  }
  function parseTerm() {
    let node = parsePower();
    while (peek().type === 'STAR' || peek().type === 'SLASH') {
      const op = next().type;
      node = { type: 'binop', op, left: node, right: parsePower() };
    }
    return node;
  }
  function parsePower() {
    const node = parseUnary();
    if (peek().type === 'CARET') {
      next();
      return { type: 'binop', op: 'CARET', left: node, right: parsePower() };
    }
    return node;
  }
  function parseUnary() {
    if (peek().type === 'MINUS') { next(); return { type: 'unary', op: 'MINUS', arg: parseUnary() }; }
    if (peek().type === 'PLUS') { next(); return parseUnary(); }
    return parsePrimary();
  }
  function parsePrimary() {
    const t = peek();
    if (t.type === 'NUMBER') { next(); return { type: 'num', value: t.value }; }
    if (t.type === 'LPAREN') { next(); const node = parseExpr(); expect('RPAREN'); return node; }
    if (t.type === 'CELL') {
      next();
      if (peek().type === 'COLON') {
        next();
        const to = expect('CELL');
        return { type: 'range', from: t.value, to: to.value };
      }
      return { type: 'cell', ref: t.value };
    }
    if (t.type === 'IDENT') {
      next();
      expect('LPAREN');
      const args = [];
      if (peek().type !== 'RPAREN') {
        args.push(parseArg());
        while (peek().type === 'COMMA') { next(); args.push(parseArg()); }
      }
      expect('RPAREN');
      return { type: 'call', name: t.value, args };
    }
    throw new FormulaError('#ERROR!');
  }
  function parseArg() {
    if (peek().type === 'CELL') {
      const save = pos;
      const cellTok = next();
      if (peek().type === 'COLON') {
        next();
        const to = expect('CELL');
        return { type: 'range', from: cellTok.value, to: to.value };
      }
      pos = save;
    }
    return parseExpr();
  }

  const result = parseExpr();
  expect('EOF');
  return result;
}

// ---- Evaluator ----

function evaluateExpr(node, ctx) {
  switch (node.type) {
    case 'num': return node.value;
    case 'cell': { const [r, c] = cellRefToRC(node.ref); return ctx.getScalar(r, c); }
    case 'range': throw new FormulaError('#VALUE!'); // a bare range only makes sense as a function argument
    case 'unary': { const v = evaluateExpr(node.arg, ctx); return node.op === 'MINUS' ? -v : v; }
    case 'binop': {
      const l = evaluateExpr(node.left, ctx);
      const r = evaluateExpr(node.right, ctx);
      if (node.op === 'PLUS') return l + r;
      if (node.op === 'MINUS') return l - r;
      if (node.op === 'STAR') return l * r;
      if (node.op === 'SLASH') { if (r === 0) throw new FormulaError('#DIV/0!'); return l / r; }
      if (node.op === 'CARET') return Math.pow(l, r);
      throw new FormulaError('#ERROR!');
    }
    case 'call': return evaluateCall(node, ctx);
    default: throw new FormulaError('#ERROR!');
  }
}

// Evaluates a function argument node - a range becomes an array of numbers
// (blanks and non-numeric text silently excluded, matching how SUM/AVERAGE
// treat a range in Excel); anything else is a single number.
function evalArgNode(node, ctx) {
  if (node.type === 'range') {
    const [r1, c1] = cellRefToRC(node.from);
    const [r2, c2] = cellRefToRC(node.to);
    return ctx.getRange(r1, c1, r2, c2);
  }
  return evaluateExpr(node, ctx);
}

function flattenArgs(args, ctx) {
  const out = [];
  args.forEach(a => {
    const v = evalArgNode(a, ctx);
    if (Array.isArray(v)) out.push(...v); else out.push(v);
  });
  return out;
}

// IRR treats the first cashflow as occurring at time 0 (undiscounted) -
// deliberately different from NPV below, which discounts its first value by
// one period. This is the same asymmetry Excel's own NPV/IRR have, and it's
// a common exam-technique trap across the financial-management papers, so it
// needs to be right rather than "close enough".
function npvFromTimeZero(rate, cashflows) {
  return cashflows.reduce((sum, cf, i) => sum + cf / Math.pow(1 + rate, i), 0);
}

function npvDerivativeFromTimeZero(rate, cashflows) {
  return cashflows.reduce((sum, cf, i) => sum - (i * cf) / Math.pow(1 + rate, i + 1), 0);
}

function computeIRR(cashflows, guess) {
  let rate = guess;
  for (let iter = 0; iter < 50; iter++) {
    const npv = npvFromTimeZero(rate, cashflows);
    const derivative = npvDerivativeFromTimeZero(rate, cashflows);
    if (Math.abs(derivative) < 1e-10) break;
    const nextRate = rate - npv / derivative;
    if (!isFinite(nextRate)) break;
    if (Math.abs(nextRate - rate) < 1e-9) return nextRate;
    rate = nextRate;
  }
  // Newton didn't converge (common with unusual cashflow patterns) - fall
  // back to a bisection scan across a wide, sane rate range.
  let low = -0.99, high = 10;
  let npvLow = npvFromTimeZero(low, cashflows);
  let npvHigh = npvFromTimeZero(high, cashflows);
  if (npvLow * npvHigh > 0) throw new FormulaError('#NUM!');
  for (let iter = 0; iter < 200; iter++) {
    const mid = (low + high) / 2;
    const npvMid = npvFromTimeZero(mid, cashflows);
    if (Math.abs(npvMid) < 1e-9) return mid;
    if ((npvLow < 0) === (npvMid < 0)) { low = mid; npvLow = npvMid; } else { high = mid; }
  }
  return (low + high) / 2;
}

// Standard normal cumulative distribution (Excel's NORMSDIST) - the
// Abramowitz & Stegun 7.1.26 erf approximation, accurate to about 1e-7,
// far finer than the 4 decimal places ACCA's normal distribution tables use.
function normsdist(z) {
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const erf = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return z >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
}

function oneNumberArg(args, ctx) {
  if (args.length !== 1) throw new FormulaError('#VALUE!');
  return evaluateExpr(args[0], ctx);
}

const FUNCTIONS = {
  SUM: (args, ctx) => flattenArgs(args, ctx).reduce((a, b) => a + b, 0),
  AVERAGE: (args, ctx) => {
    const flat = flattenArgs(args, ctx);
    if (!flat.length) throw new FormulaError('#DIV/0!');
    return flat.reduce((a, b) => a + b, 0) / flat.length;
  },
  ROUND: (args, ctx) => {
    if (args.length !== 2) throw new FormulaError('#VALUE!');
    const num = evaluateExpr(args[0], ctx);
    const digits = evaluateExpr(args[1], ctx);
    const factor = Math.pow(10, digits);
    return Math.round(num * factor) / factor;
  },
  POWER: (args, ctx) => {
    if (args.length !== 2) throw new FormulaError('#VALUE!');
    return Math.pow(evaluateExpr(args[0], ctx), evaluateExpr(args[1], ctx));
  },
  // Excel/ACCA convention: rate applies from period 1, not period 0 - the
  // time-0 outflow (if any) is added to the result separately by the
  // student, same as in the real exam tool.
  NPV: (args, ctx) => {
    if (args.length < 2) throw new FormulaError('#VALUE!');
    const rate = evaluateExpr(args[0], ctx);
    const cashflows = flattenArgs(args.slice(1), ctx);
    return cashflows.reduce((sum, cf, i) => sum + cf / Math.pow(1 + rate, i + 1), 0);
  },
  SQRT: (args, ctx) => {
    const x = oneNumberArg(args, ctx);
    if (x < 0) throw new FormulaError('#NUM!');
    return Math.sqrt(x);
  },
  // Counts the numbers among its arguments - text and blanks are skipped,
  // including a single cell holding text (counted as 0, not an error).
  COUNT: (args, ctx) => args.reduce((count, a) => {
    if (a.type === 'range' || a.type === 'cell') {
      const [r1, c1] = cellRefToRC(a.type === 'range' ? a.from : a.ref);
      const [r2, c2] = cellRefToRC(a.type === 'range' ? a.to : a.ref);
      return count + ctx.getRange(r1, c1, r2, c2).length;
    }
    try { evaluateExpr(a, ctx); return count + 1; } catch (e) { return count; }
  }, 0),
  LOG: (args, ctx) => {
    if (args.length < 1 || args.length > 2) throw new FormulaError('#VALUE!');
    const x = evaluateExpr(args[0], ctx);
    const base = args.length > 1 ? evaluateExpr(args[1], ctx) : 10;
    if (x <= 0 || base <= 0 || base === 1) throw new FormulaError('#NUM!');
    return Math.log(x) / Math.log(base);
  },
  LN: (args, ctx) => {
    const x = oneNumberArg(args, ctx);
    if (x <= 0) throw new FormulaError('#NUM!');
    return Math.log(x);
  },
  EXP: (args, ctx) => Math.exp(oneNumberArg(args, ctx)),
  NORMSDIST: (args, ctx) => normsdist(oneNumberArg(args, ctx)),
  // Excel's MIRR(values, finance_rate, reinvest_rate): negative flows
  // discounted at the finance rate, positive flows compounded forward at the
  // reinvestment rate, over the n-1 periods the n values span.
  MIRR: (args, ctx) => {
    if (args.length !== 3) throw new FormulaError('#VALUE!');
    const values = evalArgNode(args[0], ctx);
    const cashflows = Array.isArray(values) ? values : [values];
    const financeRate = evaluateExpr(args[1], ctx);
    const reinvestRate = evaluateExpr(args[2], ctx);
    const n = cashflows.length;
    if (n < 2) throw new FormulaError('#DIV/0!');
    const pvNegatives = cashflows.reduce((sum, cf, i) => sum + (cf < 0 ? cf / Math.pow(1 + financeRate, i) : 0), 0);
    const fvPositives = cashflows.reduce((sum, cf, i) => sum + (cf > 0 ? cf * Math.pow(1 + reinvestRate, n - 1 - i) : 0), 0);
    if (pvNegatives === 0 || fvPositives === 0) throw new FormulaError('#DIV/0!');
    return Math.pow(fvPositives / -pvNegatives, 1 / (n - 1)) - 1;
  },
  IRR: (args, ctx) => {
    if (!args.length) throw new FormulaError('#VALUE!');
    const values = evalArgNode(args[0], ctx);
    const cashflows = Array.isArray(values) ? values : [values];
    if (!cashflows.length) throw new FormulaError('#VALUE!');
    const guess = args.length > 1 ? evaluateExpr(args[1], ctx) : 0.1;
    return computeIRR(cashflows, guess);
  }
};

function evaluateCall(node, ctx) {
  const fn = FUNCTIONS[node.name];
  if (!fn) throw new FormulaError('#NAME?');
  return fn(node.args, ctx);
}

/**
 * Evaluates every cell in a grid (rows x cols of {value,...} cells - see
 * ExamSpreadsheetGrid's cell shape), resolving formulas (cells whose value
 * starts with "=") including references to other formula cells, with cycle
 * detection. Returns a same-shaped 2D array of
 * { display, isError, numeric, isBlank, isFormula } - `display` is what a
 * non-active cell should show (the computed result, or the raw text for an
 * ordinary value cell); the active/focused cell should instead show the raw
 * `cell.value` so it can be edited, exactly like Excel.
 */
export function evaluateGrid(grid) {
  const memo = new Map();
  const PENDING = Symbol('pending');

  function evalCell(r, c) {
    const key = `${r},${c}`;
    if (memo.has(key)) {
      const cached = memo.get(key);
      if (cached === PENDING) throw new FormulaError('#CIRC!');
      return cached;
    }
    memo.set(key, PENDING);
    const cell = grid[r]?.[c];
    const raw = (cell && typeof cell === 'object' ? cell.value : cell) || '';
    let result;
    if (typeof raw === 'string' && raw.trim().startsWith('=')) {
      try {
        const ast = parseFormula(raw.trim().slice(1));
        const value = evaluateExpr(ast, { getScalar, getRange });
        result = { display: formatNumber(value), isError: false, numeric: value, isBlank: false, isFormula: true };
      } catch (e) {
        const code = e instanceof FormulaError ? e.code : '#ERROR!';
        result = { display: code, isError: true, numeric: null, isBlank: false, isFormula: true };
      }
    } else if (!raw || !String(raw).trim()) {
      result = { display: '', isError: false, numeric: null, isBlank: true, isFormula: false };
    } else {
      // display stays exactly as typed ("970,000" still shows as 970,000)
      const numeric = parseTypedNumber(raw);
      result = { display: String(raw), isError: false, numeric, isBlank: false, isFormula: false };
    }
    if (!result.isError && result.numeric !== null && cell && typeof cell === 'object' && isNumFormat(cell.numFormat)) {
      result = { ...result, display: formatWithNumFormat(result.numeric, cell.numFormat) };
    }
    memo.set(key, result);
    return result;
  }

  function getScalar(r, c) {
    const res = evalCell(r, c);
    if (res.isError) throw new FormulaError(res.display);
    if (res.isBlank) return 0;
    if (res.numeric === null) throw new FormulaError('#VALUE!');
    return res.numeric;
  }

  function getRange(r1, c1, r2, c2) {
    const out = [];
    for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) {
      for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
        const res = evalCell(r, c);
        if (res.isError) throw new FormulaError(res.display);
        if (res.numeric !== null) out.push(res.numeric);
      }
    }
    return out;
  }

  return grid.map((row, r) => row.map((_, c) => {
    try {
      return evalCell(r, c);
    } catch (e) {
      const code = e instanceof FormulaError ? e.code : '#ERROR!';
      return { display: code, isError: true, numeric: null, isBlank: false, isFormula: true };
    }
  }));
}
